import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../assets";
import { useDispatch, useSelector } from "react-redux";
import { setSpinComplete } from "../../../State/Slices/spinSlice";
import { playSound } from "../../../State/Slices/soundsSlice";
import { Symbol } from "../../../Elements/Symbol/Symbol";
import { symbolData } from "../../../SymbolData";
import MainReelController from "./MainReelController";

const MainReel = forwardRef(
  ({ reel, index, symbolSize, handleSetCurrentWinningLine }, ref) => {
    const dispatch = useDispatch();
    const { currentRound } = useSelector((state) => state.game);
    const { isFreeSpinMode } = useSelector((state) => state.freeSpins);
    const reelController = useRef(null);
    const symbolsContainerRef = useRef(null);
    const symbolsSpriteSheet = PIXI.Assets.get(ASSET_NAME.symbols);

    // Guard: Prevent even initializing spine assets in free spin mode
    const symbolsSpineData = useMemo(() => {
      if (isFreeSpinMode) return {}; // Empty object when in Free Spin Mode

      const spineReferences = {};
      for (let i = 1; i <= 13; i++) {
        const symbolKey = `sym${String(i).padStart(2, "0")}`;
        const assetName = ASSET_NAME[symbolKey];
        const spineData = PIXI.Assets.get(assetName);
        if (!spineData || !spineData.spineData) {
          console.warn(`Failed to load Spine data for symbol: ${symbolKey}`);
          continue;
        }
        spineReferences[symbolKey] = spineData.spineData;
      }
      return spineReferences;
    }, [isFreeSpinMode]);

    const handleReelSpinComplete = useCallback(() => {
      if (isFreeSpinMode) return; // ✅ Skip logic in free spin mode
      dispatch(setSpinComplete(true));
    }, [dispatch, isFreeSpinMode]);

    const handlePlaySound = useCallback(
      (soundName) => {
        if (isFreeSpinMode) return; // ✅ Skip logic in free spin mode
        dispatch(playSound(soundName));
      },
      [dispatch, isFreeSpinMode]
    );

    useEffect(() => {
      if (isFreeSpinMode) return; // ✅ Prevent initializing the reel controller

      if (
        symbolsContainerRef.current &&
        !reelController.current &&
        symbolsSpriteSheet
      ) {
        reelController.current = new MainReelController(
          symbolsContainerRef.current,
          symbolsSpriteSheet,
          symbolsSpineData,
          currentRound?.reels,
          index,
          symbolSize,
          handleReelSpinComplete,
          handlePlaySound,
          handleSetCurrentWinningLine
        );
      }
    }, [
      index,
      symbolsSpriteSheet,
      symbolsSpineData,
      currentRound,
      symbolSize,
      handleReelSpinComplete,
      handlePlaySound,
      handleSetCurrentWinningLine,
      isFreeSpinMode,
    ]);

    useEffect(() => {
      if (isFreeSpinMode || !reelController.current) return; // ✅ Skip updates
      reelController.current.updateReels(currentRound?.reels?.[index]);
    }, [currentRound, index, isFreeSpinMode]);

    useEffect(() => {
      if (isFreeSpinMode || !reelController.current) return; // ✅ Skip size updates
      reelController.current.resizeSymbol(symbolSize);
    }, [symbolSize, isFreeSpinMode]);

    useImperativeHandle(ref, () => ({
      startSpin: (spinTime, onComplete) => {
        if (isFreeSpinMode) return; // ✅ Prevent starting spin in free spin mode
        reelController.current?.spin(spinTime, onComplete);
      },
      stopSpin: () => {
        if (isFreeSpinMode) return; // ✅ Prevent stopping spin in free spin mode
        reelController.current?.stopSpin();
      },
      getReelController: () => {
        return isFreeSpinMode ? null : reelController.current; // ✅ Return null if in free spin mode
      },
    }));

    return (
      <Container
        ref={symbolsContainerRef}
        name="SymbolsContainer"
        zIndex={2}
        sortableChildren
        y={0}
        x={0}
      >
        {reel.map((symbol, idx) => {
          const texture =
            symbolsSpriteSheet.textures[`${symbolData[symbol].name}.png`];
          const spineData = symbolsSpineData[symbolData[symbol].name];

          return (
            <Symbol
              key={idx}
              symbolSize={symbolSize}
              index={idx}
              texture={texture}
              spineData={spineData}
              showSpine={0}
            />
          );
        })}
      </Container>
    );
  }
);

export default MainReel;
